











































































import _ from 'lodash'
import Vue from 'vue'
import Component from 'vue-class-component'
import {Action, Getter, Mutation, State} from 'vuex-class'
import {MediaUpload, QueueState} from '@/modules/media/types'
import MediaDetail from '@/modules/media/components/MediaDetail.vue'
import {Prop} from 'vue-property-decorator'
import {PushFilesResult} from '@/modules/media/store/actions';
import {Colors, notify} from '@/modules/core/notification';
import MediaUploadItem from '@/modules/media/components/MediaUploadItem.vue';
import filesize from 'filesize'


/**
 * Administrador para la subida de archivos
 *
 * Muestra una pantalla donde se pueden arrastrar archivos que serán añadidos
 * a la librería multimedia de la aplicación
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {
        MediaUploadItem,
        'metadata-info': MediaDetail,
    },
    filters: {
        round: (value: any) => Math.ceil(value),
    },
})
export default class UploadManager extends Vue {
    @Prop({type: Boolean, default: false})
    public embedded: boolean

    /**
     * Cola de ficheros
     *
     * Lista de ficheros que el usuario ha añadido para subir al servidor.
     *
     * @var { MediaUpload[] }
     */
    @State(state => state.media.uploadQueue)
    public queue: MediaUpload[]

    /**
     * Procesado cola
     *
     * Indica si la aplicación se encuentra subiendo los ficheros al servidor.
     * Si es asi los botones de la interfaz son deshabilitados y no se
     * pueden subir ficheros.
     *
     * @var { boolean }
     */
    @State(state => state.media.processing)
    public isProcessing: boolean

    /**
     * Mutación del estado de la aplicación que permite mostrar u ocultar el
     * administrador de subidas
     *
     * @var { boolean }
     */
    @Mutation('media/uploadManager/show')
    public showUploadManager: (show: boolean) => void

    @Mutation('media/mediaUpload/field/update')
    public updateMediaUploadField: (payload: { id: string, field: string, value: any }) => void

    @Mutation('media/mediaUpload/thumbnail/set')
    public setMediaUploadThumbnail: (payload: { id: string, dataURL: string }) => void

    @Mutation('media/uploadQueue/clear')
    public clearUploadQueue: () => void

    @Getter('media/getMediaUploadByFile')
    public getMediaUploadByFile: (file: File) => MediaUpload | null

    @Getter('media/currentUpload')
    public currentUpload: MediaUpload

    @Getter('media/hasMetadataPendingToSend')
    public hasMetadataPendingToSend: boolean

    /**
     * Insertar archivos en la cola subida
     */
    @Action('media/pushFiles')
    public pushFilesAction: (files: FileList) => Promise<PushFilesResult>

    @Action('media/processQueue')
    public processQueue: () => Promise<void>

    /**
     * Número de ficheros que se pueden subir
     */
    @Prop({type: Number, default: 25})
    public maxFiles: number

    /**
     * Indica si se debe mostrar la barra del título de la pantalla
     */
    @Prop({type: Boolean, default: false})
    public hideToolbar: boolean

    /**
     * Indica si el administrador debe mostrar la barra de acciones inferior
     */
    @Prop({type: Boolean, default: false})
    public hideActions: boolean

    @State(state => state.media.queueState)
    public queueState: QueueState

    public loading = true

    public get canUpload () {
        return !!this.queue.length && this.queueState === QueueState.Open
    }

    public get inputAcceptTypes () {
        return this.$store.state.media.validFileTypes.join(',')
    }

    /**
     * Solicita a la aplicación la inserción de nuevos archivos en la
     * cola de subidas que será registrada en el servidor
     *
     * @param files { FileList }
     */
    public async pushFiles (files: FileList) {
        if (!files || !files.length) {
            return
        }

        if (this.queue.length >= this.maxFiles) {
            const message = this.maxFiles === 1
                ? 'Solo se puede subir un archivo'
                : `Solo de pueden subir ${this.maxFiles} archivos`

            notify(message, Colors.Warning)

            return
        }

        const result = await this.pushFilesAction(files)
        if (result.hasRejected) {
            notify('Algunos archivos no se ha añadido a la cola de subida', Colors.Warning)
        }
    }

    /**
     * Método invocado cuando el usuario selecciona ficheros desde el dialogo del explorador
     * de archivos abierto por el input oculto.
     *
     * Añade los ficheros seleccionados a la cola de subida
     */
    public onHiddenFileInput (event: InputEvent) {
        const target = event.target as HTMLInputElement
        if (!target.files) {
            return
        }

        this.pushFiles(target.files)
    }

    /**
     * onDrop
     *
     * Método invocado cuando el usuario suelta archivos en el administrador de subidas.
     * Inserta los archivos en la cola de subida.
     */
    public onDrop (event: DragEvent) {
        if (
            event.type !== 'drop'
            || !event.dataTransfer
            || !event.dataTransfer.files.length
            || this.loading
        ) { return }

        this.pushFiles(event.dataTransfer.files)
    }

    /**
     * Cierra el administrador de subidas
     */
    public close () {
        this.showUploadManager(false)

        // Limpia la cola y los archivos por subir
        this.clear()
    }

    /**
     * Limpia los archivos pendientes de subir en Dropzone y la cola de subidas
     * del estado de la app
     */
    public clear () {
        // Se retrasa el vaciado de la cola de subida para evitar que la pantalla se
        // quede vacía durante la transición de cierre
        _.delay(() => this.clearUploadQueue(), 1000)
    }

    public async created () {
        await this.$store.dispatch('media/loadUploadConstrains')
        this.loading = false
    }

    public get formattedMaxFileSize (): string {
        return filesize(this.$store.state.media.maxFileSize, {exponent: 2})
    }


    public errorCaptured () {
        this.close()
        this.loading = false
    }

    /**
     * Procede a la subida de los archivos
     */
    public async upload () {
        await this.processQueue()
        // Notifica al componente padre que el proceso de subida se ha completado
        this.$emit('uploadComplete', {
            result: 'OK',
            uploads: this.queue,
        })
    }
}
